<template>
  <section class="tk-sec" :class="{ 'tk-sec--closed': collapsed }" data-test="tasks-section" :data-section="sectionKey">
    <header class="tk-sec__head">
      <button
        v-if="collapsible"
        type="button"
        class="tk-sec__toggle"
        :aria-expanded="collapsed ? 'false' : 'true'"
        data-test="tasks-section-toggle"
        @click="collapsed = !collapsed"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" class="tk-sec__svg" aria-hidden="true" focusable="false">
          <path :d="collapsed ? 'm9 18 6-6-6-6' : 'm6 9 6 6 6-6'" />
        </svg>
        <span v-if="dot" class="tk-sec__dot" :class="`tk-sec__dot--${dot}`" aria-hidden="true"></span>
        <h2 class="tk-sec__title tk-sec__title--sm">{{ title }}</h2>
        <span class="tk-sec__count" data-test="tasks-section-count">{{ rows.length }}</span>
        <span v-if="hint" class="tk-sec__hint">{{ hint }}</span>
      </button>
      <template v-else>
        <h2 class="tk-sec__title">{{ title }}</h2>
        <span v-if="subtitle" class="tk-sec__sub" data-test="tasks-section-sub">{{ subtitle }}</span>
      </template>
    </header>

    <div v-if="!collapsed" class="tk-sec__body">
      <task-row
        v-for="t in shown"
        :key="t.id"
        :task="t"
        :ctx="ctx"
        :employees="employees"
        :show-due="showDue"
        :hide-self="hideSelf"
        :movable="movableIds ? movableIds.indexOf(t.id) > -1 : false"
        :highlight="flashId !== null && flashId === t.id"
        :complete-delay="completeDelay"
        @open="$emit('open', $event)"
        @complete="$emit('complete', $event)"
        @move="$emit('move', $event)"
      />
      <p v-if="!rows.length && emptyText" class="tk-sec__empty" data-test="tasks-section-empty">{{ emptyText }}</p>
      <button
        v-if="linkText"
        type="button"
        class="tk-sec__more tk-sec__more--link"
        data-test="tasks-section-link"
        @click="$emit('link')"
      >
        {{ linkText }}
        <svg viewBox="0 0 24 24" width="14" height="14" class="tk-sec__svg" aria-hidden="true" focusable="false">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>
      <button
        v-else-if="hiddenCount > 0"
        type="button"
        class="tk-sec__more"
        data-test="tasks-section-more"
        @click="expanded = true"
      >
        Show {{ hiddenCount }} more
      </button>
    </div>
  </section>
</template>

<script>
/**
 * TasksSection — a titled group of task rows on the redesigned Tasks page (design "Tasks 2a",
 * Phase B1, 2026-10-06).
 *
 * Two looks:
 *   • My day ("Today · 4 to go", "Coming up · Next 7 days", "Done today · 3 finished"): a large
 *     title and a muted subtitle; `limit` + `link-text` draw "See all N in Upcoming" (emits `link`).
 *   • the list views (`collapsible`): chevron + coloured dot + title + count + hint, collapsible,
 *     first `limit` rows then "Show N more".
 *
 * Props in, events out (`open(task)`, `complete(change)`, `move(task)`, `link`). Rows are TaskRow.
 */
import TaskRow from './TaskRow.vue';

export default {
  name: 'TasksSection',
  components: { TaskRow },
  props: {
    sectionKey: { type: String, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    hint: { type: String, default: '' },
    // 'late' | 'today' | 'muted' | 'done' — the list views' group dot.
    dot: { type: String, default: '' },
    rows: { type: Array, default: () => [] },
    ctx: { type: Object, required: true },
    employees: { type: Array, default: () => [] },
    // How many rows show before "Show N more" (or before the link); 0 = all.
    limit: { type: Number, default: 0 },
    // When set, replaces "Show N more" with this link (emits `link`) — shown only when rows are hidden.
    moreLinkText: { type: String, default: '' },
    collapsible: { type: Boolean, default: false },
    // Start folded (a collapsible section only): the patient Tasks tab's "Completed (N)".
    startCollapsed: { type: Boolean, default: false },
    emptyText: { type: String, default: '' },
    showDue: { type: Boolean, default: false },
    hideSelf: { type: Boolean, default: false },
    // Task ids that get TaskRow's "Move to today".
    movableIds: { type: Array, default: null },
    flashId: { type: [String, Number], default: null },
    completeDelay: { type: Number, default: 1200 },
  },

  data() {
    return { collapsed: this.collapsible && this.startCollapsed, expanded: false };
  },

  computed: {
    shown() {
      if (!this.limit || this.expanded) return this.rows;
      return this.rows.slice(0, this.limit);
    },
    hiddenCount() {
      return this.rows.length - this.shown.length;
    },
    linkText() {
      return this.moreLinkText && this.hiddenCount > 0 ? this.moreLinkText : '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.tk-sec {
  margin-top: $fu-space-8;
}

.tk-sec__head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 0 12px;
  margin-bottom: $fu-space-2;
  min-width: 0;
}

.tk-sec__toggle {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  flex-wrap: wrap;
  border-radius: $fu-radius-sm;
  color: $fu-text-muted;
}

.tk-sec__svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

.tk-sec__dot {
  @include fu-status-dot($fu-neutral-300);
  &--late { background: $fu-danger; }
  &--today { background: $fu-brand; }
  &--done { background: $fu-success; }
}

.tk-sec__title {
  @include fu-font-display;
  margin: 0;
  font-size: 19px;
  line-height: 1.25;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.tk-sec__title--sm {
  font-size: 15px;
}

.tk-sec__sub,
.tk-sec__hint {
  font-size: 13px;
  color: $fu-text-muted;
}

.tk-sec__count {
  @include fu-pill;
  @include fu-tabular;
}

.tk-sec__empty {
  margin: 0;
  padding: 10px 12px;
  font-size: $fu-fs-body-sm;
  color: $fu-text-muted;
}

.tk-sec__more {
  @include fu-link-btn;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0 44px;
  font-size: 13px;
  font-weight: $fu-fw-bold;
}

@include fu-phone {
  .tk-sec { margin-top: $fu-space-6; }
  .tk-sec__head { padding: 0 6px; }
  .tk-sec__more { margin-left: 38px; }
}
</style>
